import {
    Fab,
    createStyles,
    Modal,
    Paper,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { WarningOutlined } from "@material-ui/icons";
import React, { useState } from "react";

type ApprovalRequiredDialogProps = WithStyles<typeof styles> & {
    isApprovedAuctionBidder?: boolean;
    isAdmin?: boolean;
    isUserLoggedIn?: boolean;
};

const styles = (theme: Theme) =>
    createStyles({
        body: {
            [theme.breakpoints.down("xs")]: {
                padding: theme.spacing(2)
            },
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: theme.spacing(4),
            textAlign: "center"
        },
        bodyText: {
            [theme.breakpoints.down("xs")]: { fontSize: 20 },
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            marginBottom: theme.spacing(3),
            textAlign: "center"
        },
        bodyText2: {
            [theme.breakpoints.down("xs")]: { fontSize: 16 },
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            marginBottom: theme.spacing(3),
            textAlign: "center",
            fontWeight: "bold"
        },
        bodyText3: {
            [theme.breakpoints.down("xs")]: { fontSize: 16 },
            color: theme.palette.primary.main,
            lineHeight: 1.5,
            marginBottom: theme.spacing(3),
            textAlign: "center",
            fontWeight: "bold",
            textDecoration: "underline"
        },
        fabButton: {
            height: theme.spacing(4),
            paddingLeft: theme.spacing(5),
            paddingRight: theme.spacing(5)
        },
        header: {
            borderBottom: `8px solid ${theme.palette.primary.main}`,
            padding: theme.spacing(2),
            textAlign: "center",
            width: "100%"
        },
        headerIcon: {
            position: "relative",
            top: 3
        },
        headerText: {
            [theme.breakpoints.down("xs")]: { fontSize: 20 },
            color: theme.palette.primary.main,
            fontWeight: "bold",
            margin: "0 auto"
        },
        paper: {
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            margin: theme.spacing(2),
            maxWidth: 500,
            outline: "none",
            width: "100%"
        }
    });

function ApprovalRequiredDialog({
    classes,
    isAdmin,
    isApprovedAuctionBidder,
    isUserLoggedIn
}: ApprovalRequiredDialogProps) {
    const [isDismissed, setIsDismissed] = useState(false);

    if (isDismissed || isAdmin || isApprovedAuctionBidder || !isUserLoggedIn) {
        return null;
    }

    return (
        <Modal
            open
            className={classes.body}
            data-testid="approval-required-dialog"
        >
            <Paper className={classes.paper} elevation={2}>
                <div className={classes.header}>
                    <Typography
                        component="h1"
                        variant="h5"
                        className={classes.headerText}
                    >
                        <WarningOutlined className={classes.headerIcon} />
                        &nbsp;Approval Required
                    </Typography>
                </div>

                <div className={classes.body}>
                    <Typography
                        component="div"
                        variant="h5"
                        className={classes.bodyText}
                    >
                        You are not yet approved to bid.
                    </Typography>
                    <Typography
                        component="div"
                        variant="body1"
                        className={classes.bodyText2}
                    >
                        If you would like to participate in this auction, please
                        email{" "}
                        <a
                            href="mailto:info@clhbid.com"
                            className={classes.bodyText3}
                        >
                            info@clhbid.com
                        </a>{" "}
                        or call{" "}
                        <a
                            href="tel:+18662637480"
                            className={classes.bodyText3}
                        >
                            1-866-263-7480
                        </a>{" "}
                        for assistance.
                    </Typography>

                    <Fab
                        className={classes.fabButton}
                        variant="extended"
                        color="primary"
                        onClick={() => setIsDismissed(true)}
                    >
                        View as Guest
                    </Fab>
                </div>
            </Paper>
        </Modal>
    );
}

export default withStyles(styles)(ApprovalRequiredDialog);
